/**
 * Jobs — acompanhar (e reprocessar/cancelar) tudo que roda em background.
 *
 * Duas seções: a FILA do arq (worker vivo?, o que está em execução, na fila, os crons) e as
 * IMPORTAÇÕES (a tabela `importacoes`, com progresso por linha). Atualiza sozinha por polling.
 * Reprocessar re-enfileira uma importação travada/errada — o self-service que antes exigia a mão.
 */
import { Activity, Ban, RefreshCw, RotateCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Skeleton, Spinner } from '@/components/ui/misc';
import { Tabela, Tbody, Td, Th, Thead, Tr } from '@/components/ui/tabela';
import {
  cancelarJob,
  estadoDaFila,
  type EstadoFila,
  type ImportacaoJob,
  listarImportacoesJobs,
  reprocessarImportacao,
} from '@/lib/api';

const ROTULO_ESTADO: Record<string, string> = {
  aguardando_revisao: 'Aguardando revisão',
  processando: 'Processando',
  concluida: 'Concluída',
  erro: 'Erro',
};

/** Classe do Chip por estado da importação. */
function corEstado(estado: string): string {
  if (estado === 'concluida') return 'border-success/40 text-success';
  if (estado === 'erro') return 'border-danger/40 text-danger';
  if (estado === 'processando') return 'border-warning/40 text-warning';
  return 'text-text-tertiary';
}

/** ISO → data/hora local curta. Vazio some. */
function quando(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function BarraProgresso({ feito, total }: { feito: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((100 * feito) / total)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-hover">
        <div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="tabular-nums text-[12px] text-text-tertiary">{pct}%</span>
    </div>
  );
}

export function Jobs() {
  const [imports, setImports] = useState<ImportacaoJob[] | null>(null);
  const [fila, setFila] = useState<EstadoFila | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null); // id/job em ação (reprocessar/cancelar)
  const vivoRef = useRef(true);

  async function carregar() {
    const [ri, rf] = await Promise.all([listarImportacoesJobs(100), estadoDaFila()]);
    if (!vivoRef.current) return;
    if (ri.ok) setImports(ri.dados.itens);
    if (rf.ok) setFila(rf.dados);
  }

  useEffect(() => {
    vivoRef.current = true;
    let timer: ReturnType<typeof setTimeout>;
    const bater = async () => {
      await carregar();
      if (!vivoRef.current) return;
      timer = setTimeout(bater, 4000); // auto-refresh
    };
    void bater();
    return () => {
      vivoRef.current = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reprocessar(imp: ImportacaoJob) {
    setOcupado(imp.id);
    const r = await reprocessarImportacao(imp.id);
    setOcupado(null);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Importação re-enfileirada.');
    void carregar();
  }

  async function cancelar(jobId: string) {
    setOcupado(jobId);
    const r = await cancelarJob(jobId);
    setOcupado(null);
    if (!r.ok) return void toast.error(r.erro);
    toast[r.dados.ok ? 'success' : 'message'](r.dados.ok ? 'Cancelamento solicitado.' : 'Job não pôde ser cancelado.');
    void carregar();
  }

  return (
    <div className="flex flex-col gap-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="tipo-titulo flex items-center gap-2">
            <Activity className="size-6 text-accent" />
            Jobs
          </h1>
          <p className="mt-1 text-[15px] text-text-secondary">
            Tudo que roda em background — importações, fila do worker e tarefas agendadas. Atualiza
            sozinho.
          </p>
        </div>
        <Button variant="secundario" size="sm" onClick={() => void carregar()}>
          <RefreshCw /> Atualizar
        </Button>
      </header>

      {/* ---- Fila & Worker ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Fila &amp; Worker</span>
            {fila && (
              <Chip aceso={fila.worker_online} className={fila.worker_online ? 'border-success/40 text-success' : 'border-danger/40 text-danger'}>
                {fila.worker_online ? 'Worker online' : 'Worker offline'}
              </Chip>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {!fila ? (
            <Spinner />
          ) : (
            <>
              <Secao titulo={`Em execução (${fila.em_execucao.length})`}>
                {fila.em_execucao.length === 0 ? (
                  <Vazio>Nada rodando agora.</Vazio>
                ) : (
                  fila.em_execucao.map((j) => (
                    <LinhaJob key={j.job_id ?? j.funcao} funcao={j.funcao} sub={`desde ${quando(j.iniciado_em)}`}>
                      {j.job_id && (
                        <Button
                          variant="fantasma"
                          size="sm"
                          className="text-danger hover:text-danger"
                          disabled={ocupado === j.job_id}
                          onClick={() => void cancelar(j.job_id!)}
                        >
                          {ocupado === j.job_id ? <Spinner className="size-4" /> : <Ban />} Cancelar
                        </Button>
                      )}
                    </LinhaJob>
                  ))
                )}
              </Secao>

              <Secao titulo={`Na fila (${fila.na_fila.length})`}>
                {fila.na_fila.length === 0 ? (
                  <Vazio>Fila vazia.</Vazio>
                ) : (
                  fila.na_fila.map((j, i) => (
                    <LinhaJob key={j.job_id ?? i} funcao={j.funcao} sub={j.tentativa ? `tentativa ${j.tentativa}` : ''} />
                  ))
                )}
              </Secao>

              <Secao titulo="Tarefas agendadas (crons)">
                {fila.crons.map((cr) => (
                  <div key={cr.funcao} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-[15px] text-text">{cr.rotulo}</span>
                      <span className="tipo-corpo-pequeno text-text-tertiary">{cr.quando}</span>
                    </div>
                    <div className="shrink-0">
                      {cr.ultima ? (
                        <Chip className={cr.ultima.sucesso ? 'border-success/40 text-success' : 'border-danger/40 text-danger'}>
                          {cr.ultima.sucesso ? 'ok' : 'falhou'} · {quando(cr.ultima.terminado_em)}
                        </Chip>
                      ) : (
                        <Chip>sem execução recente</Chip>
                      )}
                    </div>
                  </div>
                ))}
              </Secao>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---- Importações ---- */}
      <Card>
        <CardHeader>
          <CardTitle>Importações</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {imports === null ? (
            <div className="flex flex-col gap-2 p-4">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : imports.length === 0 ? (
            <p className="p-4 tipo-corpo-pequeno text-text-tertiary">Nenhuma importação ainda.</p>
          ) : (
            <Tabela>
              <Thead>
                <Th>Arquivo</Th>
                <Th>Entidade</Th>
                <Th>Estado</Th>
                <Th>Progresso</Th>
                <Th>Criadas</Th>
                <Th>Erros</Th>
                <Th>Quando</Th>
                <Th> </Th>
              </Thead>
              <Tbody>
                {imports.map((imp) => {
                  const podeReprocessar = imp.estado === 'erro' || imp.estado === 'processando';
                  return (
                    <Tr key={imp.id}>
                      <Td>
                        <span className="text-text">{imp.nome_arquivo}</span>
                      </Td>
                      <Td>{imp.entidade_alvo}</Td>
                      <Td>
                        <Chip className={corEstado(imp.estado)}>{ROTULO_ESTADO[imp.estado] ?? imp.estado}</Chip>
                      </Td>
                      <Td>
                        <BarraProgresso feito={imp.processadas} total={imp.total_linhas} />
                        <span className="tabular-nums text-[12px] text-text-tertiary">
                          {imp.processadas.toLocaleString('pt-BR')}/{imp.total_linhas.toLocaleString('pt-BR')}
                        </span>
                      </Td>
                      <Td mono>{imp.criadas.toLocaleString('pt-BR')}</Td>
                      <Td mono className={imp.erros > 0 ? 'text-danger' : undefined}>
                        {imp.erros.toLocaleString('pt-BR')}
                      </Td>
                      <Td>{quando(imp.criado_em)}</Td>
                      <Td>
                        {podeReprocessar && (
                          <Button
                            variant="fantasma"
                            size="sm"
                            disabled={ocupado === imp.id}
                            onClick={() => void reprocessar(imp)}
                            title="Re-enfileirar esta importação"
                          >
                            {ocupado === imp.id ? <Spinner className="size-4" /> : <RotateCw />} Reprocessar
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Tabela>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="tipo-rotulo text-text-secondary">{titulo}</span>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

function Vazio({ children }: { children: React.ReactNode }) {
  return <p className="tipo-corpo-pequeno text-text-tertiary">{children}</p>;
}

function LinhaJob({ funcao, sub, children }: { funcao: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[15px] text-text">{funcao}</span>
        {sub && <span className="tipo-corpo-pequeno text-text-tertiary">{sub}</span>}
      </div>
      {children}
    </div>
  );
}
